// ExtVersionContext.jsx
import React, {
    createContext, useContext, useMemo, useState, useEffect,
    useCallback, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteExtVersion,
    downloadExtVersionCSV,
    getExtVersions,
    retryOutput,
    setExtVersion,
    batchStatusCheck,
    initiateDraftSession,          // new
} from '../../store/extVersions/actions';
import { capitalizeFirst } from '../../commonUtils/string.helper';
import constants from '../../commonUtils/constants';
import IconButton from '../../components/IconButton';
import Spinner from '../../components/Spinner';
// import { ArrowPathIcon, ArrowRightCircleIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { getOutputMappings } from '../../store/extOutputMappings/actions';
import { EllipsisVerticalIcon } from 'lucide-react';
import { useExtractionContext } from './ExtractionContext';
// import { setSelectedVersion} from '../../store/extractions/action';

const ExtVersionsContext = createContext();
export const useExtVersions = () => useContext(ExtVersionsContext);

// ── Statuses that mean "still in-flight" ─────────────────────────────────────
const PROCESSING_STATUSES = ['pending', 'triggered', 'processing'];

const isProcessingStatus = (status) =>
    status && PROCESSING_STATUSES.includes(status.toLowerCase());

// ── Polling config ────────────────────────────────────────────────────────────
const BATCH_POLL_INTERVAL_MS = constants.extraction.extVersion.polling.interval;       // 10 minutes between ticks
const BATCH_POLL_MAX_MS      = constants.extraction.extVersion.polling.maxTotalTime; // 30 min

export const ExtVersionsProvider = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        filters,
        ExtVersionsList,
        extVersion,
        loading,
        errors,
    } = useSelector((state) => state.ExtVersions);

    const { extractionViewOnly, extractionData } = useExtractionContext();

    // ── UI state ──────────────────────────────────────────────────────────
    const [openUpdateModal, setOpenUpdateModal]                         = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId]                                   = useState('');
    const [updateData, setUpdateData]                                   = useState({ _id: '', title: '' });
    const [perPage, setPerPage]                                         = useState(constants.tableUI.defaultPerpage);
    const [showErrorModal, setShowErrorModal]                           = useState(false);
    const [errorModalData, setErrorModalData]                           = useState(null);

    // ── Polling state (component-level — no Redux involvement) ────────────
    // isPollingActive drives the spinner / "Processing Data" label per row.
    // timedOutVersionIds drives the "Retry Output" button after the hard stop.
    const [isPollingActive, setIsPollingActive]       = useState(false);
    const [timedOutVersionIds, setTimedOutVersionIds] = useState(new Set());

    const pollingTimerRef    = useRef(null);
    const pollingStartTimeRef = useRef(null);

    // Live references — let interval callbacks read current values without
    // stale closure issues (same pattern as MCC's getCampaigns getter ref).
    const extractionIdRef      = useRef(extractionData?.id);
    const extVersionsListRef   = useRef(ExtVersionsList);

    useEffect(() => { extractionIdRef.current    = extractionData?.id;  }, [extractionData?.id]);
    useEffect(() => { extVersionsListRef.current = ExtVersionsList;      }, [ExtVersionsList]);

    // ── Misc refs ─────────────────────────────────────────────────────────
    const previousExtractionIdRef              = useRef(null);
    const refreshRetryTriggeredByExtractionRef = useRef(new Set());

    // ── stopPolling ───────────────────────────────────────────────────────
    const stopPolling = useCallback((timedOut = false) => {
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        pollingStartTimeRef.current = null;

        if (timedOut) {
            // Capture every row that is still processing at timeout time
            const timedOutIds = (extVersionsListRef.current?.data || [])
                .filter((v) => isProcessingStatus(v.process_status || v.status))
                .map((v) => v._id)
                .filter(Boolean);

            if (timedOutIds.length > 0) {
                setTimedOutVersionIds((prev) => new Set([...prev, ...timedOutIds]));
                toast.warning(
                    "Extraction is taking longer than expected. You can retry the output check manually."
                );
            }
        }

        setIsPollingActive(false);
    }, []); // no deps — reads live data through refs

    // ── startPolling ──────────────────────────────────────────────────────
    const startPolling = useCallback(() => {
        if (pollingTimerRef.current) return; // already running

        pollingStartTimeRef.current = Date.now();
        setIsPollingActive(true);

        pollingTimerRef.current = setInterval(() => {
            const extractionId = extractionIdRef.current;
            const elapsed      = Date.now() - (pollingStartTimeRef.current ?? Date.now());

            // Hard stop
            if (!extractionId || elapsed >= BATCH_POLL_MAX_MS) {
                stopPolling(Boolean(extractionId)); // timedOut=true only if we have an extraction
                return;
            }

            dispatch(batchStatusCheck(extractionId));
        }, BATCH_POLL_INTERVAL_MS);
    }, [dispatch, stopPolling]); // stable — reads extractionId via ref

    // ── Effect: auto start/stop based on processing rows ─────────────────
    useEffect(() => {
        const hasProcessing = ExtVersionsList?.data?.some((v) =>
            isProcessingStatus(v.process_status || v.status)
        );

        if (hasProcessing && !pollingTimerRef.current) {
            startPolling();
        } else if (!hasProcessing && pollingTimerRef.current) {
            stopPolling(false);
        }
    }, [ExtVersionsList?.data, startPolling, stopPolling]);

    // ── Effect: stop + reset when extraction switches ─────────────────────
    useEffect(() => {
        const currentExtractionId  = extractionData?.id || null;
        const previousExtractionId = previousExtractionIdRef.current;

        if (
            previousExtractionId &&
            currentExtractionId &&
            previousExtractionId !== currentExtractionId
        ) {
            stopPolling(false);
            setTimedOutVersionIds(new Set());
            dispatch({ type: 'RESET_EXT_VERSION' });
            refreshRetryTriggeredByExtractionRef.current.delete(currentExtractionId);
        }

        previousExtractionIdRef.current = currentExtractionId;
    }, [dispatch, extractionData?.id, stopPolling]);

    // ── Effect: cleanup on unmount ────────────────────────────────────────
    useEffect(() => () => stopPolling(false), [stopPolling]);

    // ── Data fetching ─────────────────────────────────────────────────────
    const fetchExtVersions = useCallback(
        (params) => dispatch(getExtVersions(params)),
        [dispatch]
    );

    const debouncedGetExtVersions = useMemo(
        () => debounce(fetchExtVersions, 300),
        [fetchExtVersions]
    );

    useEffect(() => {
        debouncedGetExtVersions({ currentPage: 1, perPage, ...filters });
        return () => debouncedGetExtVersions.cancel();
    }, [filters, debouncedGetExtVersions, perPage]);

    // ── Effect: post-refresh/page-reload immediate batch check ───────────
    // Fires once per extraction: if there are already-processing rows when
    // the user opens Step 4 (or reloads the page), kick off one check
    // immediately rather than waiting for the first 30-second tick.
    useEffect(() => {
        const extractionId = extractionData?.id;
        if (!extractionId || !ExtVersionsList?.data?.length) return;
        if (refreshRetryTriggeredByExtractionRef.current.has(extractionId)) return;

        const hasProcessing = ExtVersionsList.data.some((v) =>
            isProcessingStatus(v.process_status || v.status)
        );

        if (hasProcessing) {
            dispatch(batchStatusCheck(extractionId));
        }

        refreshRetryTriggeredByExtractionRef.current.add(extractionId);
    }, [dispatch, extractionData?.id, ExtVersionsList?.data]);

    // ── Close modals after list refresh ──────────────────────────────────
    useEffect(() => {
        setUpdateData({ _id: '', title: '' });
        setOpenUpdateModal(false);
        setToDeleteId('');
        setShowDeleteConfirmationDialog(false);
    }, [ExtVersionsList]);

    // ── Error modal ───────────────────────────────────────────────────────
    const viewErrorMessage = useCallback((data) => {
        setErrorModalData(data);
        setShowErrorModal(true);
    }, []);

    const handleCloseErrorModal = useCallback(() => {
        setShowErrorModal(false);
        setErrorModalData(null);
    }, []);

    const updateDataStrategy = useCallback((id) => {
        // Set expiration 10 years into the future
        dispatch(initiateDraftSession({
            extId: extractionData?.id || extractionData?._id,
            extKey: extractionData?.key,
            versionId: id,
            navigate,
        }));
    }, [dispatch, extractionData, navigate]);

    // ── extVersions derived list ──────────────────────────────────────────
    const extVersions = useMemo(() => {
        if (!ExtVersionsList?.data) return [];

        return ExtVersionsList.data.map(
            (
                {
                    _id, key, title, created_by, created_at,
                    formatted_created_at, status, output,
                    process_exec_id, process_status,
                    process_start_time, process_completion_time,
                    process_run_duration, error_message,
                    loading: versionLoading,
                },
                index
            ) => {
                // Guard: only apply single extVersion override when it belongs
                // to the current extraction (prevents stale data bleed).
                const extVersionBelongsToCurrentExtraction =
                    extVersion?.extraction_id
                        ? extVersion.extraction_id === (extractionData?.id || extractionData?._id)
                        : true;

                const isCurrentVersion =
                    extVersionBelongsToCurrentExtraction &&
                    Boolean(
                        extVersion &&
                            _id &&
                            ((extVersion.id  && _id === extVersion.id) ||
                             (extVersion.key && key && key === extVersion.key))
                    );

                // Prefer live single-version data for the selected row
                const currentStatus = isCurrentVersion && extVersion.process_status
                    ? extVersion.process_status
                    : process_status || status;

                const currentOutput =
                    isCurrentVersion && extVersion.output !== undefined
                        ? extVersion.output : output;

                const currentProcessExecId =
                    isCurrentVersion && extVersion.process_exec_id !== undefined
                        ? extVersion.process_exec_id : process_exec_id;

                // ── Polling-derived flags (context-level, not Redux) ──────
                const isStillProcessing = isProcessingStatus(currentStatus);

                // Row shows spinner while the global batch timer is running
                const isBeingPolled = isPollingActive && isStillProcessing;

                // Row was still processing when the 30-min hard stop fired
                const wasPolledAndTimedOut =
                    isStillProcessing && timedOutVersionIds.has(_id);

                // Row is very old, never polled/timed-out, still processing
                const maxTotalMinutes = BATCH_POLL_MAX_MS / (1000 * 60);
                const isOrphanedProcessing =
                    isStillProcessing &&
                    !isBeingPolled &&
                    !wasPolledAndTimedOut &&
                    created_at &&
                    (new Date() - new Date(created_at)) / (1000 * 60) > maxTotalMinutes;

                // Combined "show Retry Output button" flag
                const isProcessingStale = wasPolledAndTimedOut || isOrphanedProcessing;

                return {
                    slno:    index + 1,
                    id:      _id,
                    key,
                    title,
                    output:  currentOutput,
                    calculated_by:
                        (isCurrentVersion && extVersion.created_by?.name)
                            ? extVersion.created_by.name
                            : created_by?.name,
                    calculated_on: formatted_created_at,
                    status:        capitalizeFirst(currentStatus),
                    exec_id:       currentProcessExecId,
                    process_status: currentStatus,
                    process_start_time:
                        isCurrentVersion && extVersion.process_start_time
                            ? extVersion.process_start_time : process_start_time,
                    process_completion_time:
                        isCurrentVersion && extVersion.process_completion_time
                            ? extVersion.process_completion_time : process_completion_time,
                    process_run_duration:
                        isCurrentVersion && extVersion.process_run_duration
                            ? extVersion.process_run_duration : process_run_duration,
                    selected_columns:
                        isCurrentVersion && extVersion.selected_columns
                            ? extVersion.selected_columns : [],
                    custom_columns:
                        isCurrentVersion && extVersion.custom_columns
                            ? extVersion.custom_columns : [],
                    error_message:
                        isCurrentVersion && extVersion.error_message
                            ? extVersion.error_message : error_message,
                    isBeingPolled,
                    isProcessingStale,
                    loading: versionLoading || (isCurrentVersion && extVersion.loading) || false,
                };
            }
        );
    }, [
        ExtVersionsList,
        extVersion,
        extractionData?.id,
        extractionData?._id,
        isPollingActive,
        timedOutVersionIds,
    ]);

    // ── Pagination ────────────────────────────────────────────────────────
    const pagination = useMemo(() => ExtVersionsList?.pagination, [ExtVersionsList?.pagination]);

    const handlePageChange = useCallback(
        (currentPage, newPerPage) => {
            if (newPerPage !== perPage) setPerPage(newPerPage);
            debouncedGetExtVersions({ currentPage, perPage: newPerPage, ...filters });
        },
        [debouncedGetExtVersions, filters, perPage]
    );

    // ── CRUD helpers ──────────────────────────────────────────────────────
    const editExtVersion = useCallback((data) => {
        setUpdateData(data);
        setOpenUpdateModal(true);
    }, []);

    const handleCloseModal = useCallback((action) => {
        if (action === 'update') {
            setUpdateData({ _id: '', title: '' });
            setOpenUpdateModal(false);
        } else if (action === 'delete') {
            setToDeleteId('');
            setShowDeleteConfirmationDialog(false);
        }
    }, []);

    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const handleDelete = useCallback(() => {
        dispatch(deleteExtVersion(toDeleteId));
        handleCloseModal('delete');
    }, [dispatch, toDeleteId, handleCloseModal]);

    const viewExtraction = useCallback(
        (data) => {
            const { id, key } = data;
            dispatch(setExtVersion({ ...data }));
            dispatch(getOutputMappings({ id, key, navigate }));
        },
        [dispatch, navigate]
    );

    const handleDownload = useCallback(
        (key) => dispatch(downloadExtVersionCSV(key)),
        [dispatch]
    );

    const reprocessOutput = useCallback(
        (id) => dispatch(retryOutput(id)),
        [dispatch]
    );

    /* const proceedWithSelectedVersion = useCallback(
        (versionId) => {
            dispatch(setSelectedVersion({ extractionId: extractionData?.id, versionId }));
        },
        [dispatch, extractionData?.id]
    ); */

    // ── Table columns ─────────────────────────────────────────────────────
    const columnHelper = createColumnHelper();

    const columns = useMemo(
        () => [
            columnHelper.accessor('slno', {
                header: '#',
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('key', {
                header: 'Key',
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('title', {
                header: 'Version',
                cell: (info) => (
                    <div className="flex items-center justify-center gap-2">
                        {info.getValue()}
                        {info.row.original.isBeingPolled ? <Spinner /> : null}
                    </div>
                ),
            }),
            columnHelper.accessor('calculated_on', {
                header: 'Calculated On',
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('calculated_by', {
                header: 'Calculated By',
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: (info) => {
                    const raw = info.getValue();
                    return ['Pending', 'Triggered', 'Processing'].includes(raw)
                        ? 'Processing'
                        : raw;
                },
            }),
            columnHelper.accessor('actions', {
                header: 'Actions',
                cell: (info) => {
                    const row       = info.row.original;
                    const isRetrying = Boolean(row.loading);
                    const showErrorIcon = row.status === 'Failed' && row.error_message;

                    return (
                        <>

                            {row.status === "Completed" && (
                                <>
                                    <IconButton
                                        variant="action_default" size="icon"
                                        Icon={ArrowDownTrayIcon} disabled={isRetrying}
                                        onClick={() => handleDownload(row.key)}
                                    />
                                    <IconButton
                                        variant="action_default" size="icon"
                                        Icon={EyeIcon} disabled={isRetrying}
                                        onClick={() => viewExtraction(row)}
                                    />
                                </>
                            )}

                            {['Completed', 'Failed'].includes(row.status) && !extractionViewOnly && (
                                <IconButton
                                    variant="action_default" size="icon"
                                    Icon={PencilSquareIcon}
                                    onClick={() => updateDataStrategy(row.id)}
                                />
                            )}

                            {/* {row.status === 'Completed' && !extractionViewOnly && (
                                <>
                                    <IconButton
                                        variant="action_default" size="icon"
                                        disabled={isRetrying}
                                        Icon={ArrowRightCircleIcon}
                                        onClick={() => proceedWithSelectedVersion(row.id)}
                                    />
                                </>
                            )} */}

                            {(row.isProcessingStale ||
                                (row.status === 'Timed out' && row.exec_id)) && (
                                <IconButton
                                    variant="action_default" size="icon"
                                    Icon={isRetrying ? Spinner : ArrowPathIcon}
                                    disabled={isRetrying}
                                    onClick={() => reprocessOutput(row.id)}
                                    title="Retry output check"
                                />
                            )}

                            {showErrorIcon && (
                                <IconButton
                                    variant="action_default" size="icon"
                                    Icon={EllipsisVerticalIcon}
                                    onClick={() => viewErrorMessage(row)}
                                    title="View error details"
                                />
                            )}

                            {!row.isProcessingStale &&
                                row.status !== 'Timed out' &&
                                row.isBeingPolled && (
                                    <p className="text-gray-500">Processing Data</p>
                                )}

                            {!row.isProcessingStale &&
                                !row.isBeingPolled &&
                                ['Pending', 'Triggered', 'Processing'].includes(row.status) && (
                                    <p className="text-gray-500">Processing Data</p>
                                )}
                        </>
                    );
                },
            }),
        ],
        [
            columnHelper,
            handleDownload,
            viewExtraction,
            reprocessOutput,
            viewErrorMessage,
            extractionViewOnly,
            // proceedWithSelectedVersion,
            updateDataStrategy,
        ]
    );

    // ── Table instance ────────────────────────────────────────────────────
    const table = useReactTable({
        data: extVersions || [],
        columns,
        getCoreRowModel:       getCoreRowModel(),
        getSortedRowModel:     getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: {
            pagination: {
                pageIndex: (ExtVersionsList?.pagination?.currentPage || 1) - 1,
                pageSize:  perPage,
            },
        },
        manualPagination: true,
        pageCount: ExtVersionsList?.pagination?.totalPages || 0,
    });

    // ── Context value ─────────────────────────────────────────────────────
    return (
        <ExtVersionsContext.Provider
            value={{
                extVersions,
                table,
                columns,
                loading,
                errors,
                pagination,
                handlePageChange,
                editExtVersion,
                openUpdateModal,
                setOpenUpdateModal,
                updateData,
                handleCloseModal,
                showDeleteConfirmationDialog,
                askDeleteConfirmation,
                handleDelete,
                viewErrorMessage,
                showErrorModal,
                errorModalData,
                handleCloseErrorModal,
            }}
        >
            {children}
        </ExtVersionsContext.Provider>
    );
};